import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { Target, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import MetaProgressBar from "../../components/MetaProgressBar";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function MetasUnidade() {
  const { unidade, theme } = useOutletContext();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/metas/ativa", { params: { unidade_id: unidade.id } })
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Sem meta ativa"));
  }, [unidade.id]);

  if (error && !meta) {
    return (
      <div>
        <div className="mb-6">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Performance
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Metas
          </h2>
        </div>
        <div className="bg-white border-2 border-neutral-900 p-8 text-center">
          <Target className="w-10 h-10 mx-auto mb-3 text-neutral-400" />
          <h3 className="font-display font-bold text-lg">Nenhuma meta ativa</h3>
          <p className="text-sm text-neutral-600 mt-1">
            Peça ao Painel Master para definir uma meta para esta unidade.
          </p>
        </div>
      </div>
    );
  }

  if (!meta) return <div className="text-sm text-neutral-500">Carregando...</div>;

  const bate = meta.bate_meta_tendencia;
  // meta acumulada linear p/ referência no gráfico
  const metaDiaria = meta.total_dias > 0 ? meta.meta_faturamento / meta.total_dias : 0;
  const serieComMeta = meta.serie_diaria.map((d, i) => ({
    ...d,
    meta_acum: Math.round(metaDiaria * (i + 1) * 100) / 100,
  }));

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Performance · {unidade.nome}
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          {meta.titulo}
        </h2>
        <p className="font-mono text-sm text-neutral-600 mt-1">
          {new Date(meta.periodo_inicio + "T00:00").toLocaleDateString("pt-BR")} →{" "}
          {new Date(meta.periodo_fim + "T00:00").toLocaleDateString("pt-BR")} ·{" "}
          Dia {meta.dias_decorridos} de {meta.total_dias}
        </p>
      </div>

      <div
        className={`border-4 p-4 mb-6 ${
          bate ? "border-green-600 bg-green-50" : "border-red-600 bg-red-50"
        }`}
      >
        <div className="flex items-start gap-3">
          {bate ? (
            <TrendingUp className="w-6 h-6 text-green-700 flex-shrink-0 mt-1" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
          )}
          <div className="flex-1">
            <div className="font-display font-black text-lg">
              {bate ? "Tendência positiva — meta será atingida" : "⚠ Abaixo do ritmo necessário"}
            </div>
            <div className="text-sm text-neutral-700 mt-1">
              Projeção mantendo ritmo atual:{" "}
              <strong>{money(meta.projecao_final)}</strong> (
              {meta.meta_faturamento > 0
                ? ((meta.projecao_final / meta.meta_faturamento) * 100).toFixed(1)
                : 0}
              % da meta)
            </div>
            <div className="text-sm text-neutral-700 mt-1">
              Faturamento diário necessário para bater a meta:{" "}
              <strong>{money(meta.faturamento_diario_necessario)}</strong> (restam{" "}
              {meta.dias_restantes} dias).
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Metric label="Atual" value={money(meta.faturamento_atual)} accent={theme.primaryText} />
        <Metric label="Meta" value={money(meta.meta_faturamento)} />
        <Metric label="Falta" value={money(meta.falta)} />
        <Metric label="% Meta" value={`${meta.percentual_faturamento}%`} />
      </div>

      <div className="bg-white border-2 border-neutral-900 p-4 mb-6">
        <h3 className="font-display font-bold uppercase mb-2">Progresso por Faturamento</h3>
        <MetaProgressBar
          current={meta.faturamento_atual}
          target={meta.meta_faturamento}
          color={bate ? "bg-green-600" : "bg-red-600"}
        />
        <div className="mt-4" style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={serieComMeta}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis
                dataKey="data"
                fontSize={10}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis fontSize={10} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="acumulado"
                name="Acumulado real"
                stroke={theme.hex}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="meta_acum"
                name="Meta (linear)"
                stroke="#0A0A0A"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                dot={false}
              />
              <ReferenceLine
                y={meta.meta_faturamento}
                stroke="#eab308"
                strokeDasharray="3 3"
                label={{ value: "Meta total", position: "right", fontSize: 10 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {meta.metas_vendedores_progresso?.length > 0 && (
        <div className="bg-white border-2 border-neutral-900 p-4 mb-6">
          <h3 className="font-display font-bold uppercase mb-3">Metas de itens por vendedor</h3>
          <div className="space-y-3">
            {meta.metas_vendedores_progresso.map((v) => (
              <MetaProgressBar
                key={v.vendedor_id}
                label={`${v.vendedor_nome}`}
                current={v.qtd_atual}
                target={v.qtd_alvo}
                unit=" itens"
                color={v.percentual >= 100 ? "bg-green-600" : theme.primaryText === "text-blue-700" ? "bg-blue-600" : "bg-red-600"}
              />
            ))}
          </div>
        </div>
      )}

      {meta.metas_itens_alvo_progresso?.length > 0 && (
        <div className="bg-white border-2 border-neutral-900 p-4">
          <h3 className="font-display font-bold uppercase mb-3">Itens-alvo da unidade</h3>
          <div className="space-y-3">
            {meta.metas_itens_alvo_progresso.map((it) => (
              <MetaProgressBar
                key={it.produto_id}
                label={it.produto_nome}
                current={it.qtd_atual}
                target={it.qtd_alvo}
                unit=" un"
                color={it.percentual >= 100 ? "bg-green-600" : "bg-yellow-500"}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent = "" }) {
  return (
    <div className="bg-white border-2 border-neutral-900 p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {label}
      </div>
      <div className={`font-display font-black text-xl ${accent}`}>{value}</div>
    </div>
  );
}
